import React, { useContext, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Nav, Navbar, Row, Col } from 'react-bootstrap';
import { navbarBreakPoint, topNavbarBreakpoint } from 'config';
import AppContext from 'context/Context';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import { capitalize } from 'helpers/utils';
import NavbarTopDropDownMenus from 'components/navbar/top/NavbarTopDropDownMenus';
import bgNavbar from 'assets/img/generic/bg-navbar.png';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { WORKSPACE_MODES, getWorkspaceMode, isRouteVisibleForMode } from 'helpers/workspace';
import {
  metricsRoutes,
  marketplaceRoutes,
  operationsRoutes,
  configurationRoutes,
  accessRoutes,
  workspaceRoutes
} from 'routes/siteMaps';

const NavbarVertical = () => {
  const {
    config: {
      navbarPosition,
      navbarStyle,
      isNavbarVerticalCollapsed,
      showBurgerMenu
    }
  } = useContext(AppContext);
  const { t } = useTranslation();
  const { hasPermission, userData, isSuperAdmin } = useAuth();
  const currentMode = getWorkspaceMode(userData);

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  //Control mouseEnter event
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const filterRoutesByPermission = routeItems =>
    routeItems
      .map(route => {
        if (!isRouteVisibleForMode(currentMode, route.modes)) {
          return null;
        }

        if (route.requiresSuperAdmin && !isSuperAdmin()) {
          return null;
        }

        if (route.children) {
          const filteredChildren = filterRoutesByPermission(route.children);
          if (!filteredChildren.length) {
            return null;
          }
          return { ...route, children: filteredChildren };
        }

        if (route.requiredPermission && !hasPermission(route.requiredPermission)) {
          return null;
        }

        if (
          route.requiredAnyPermissions &&
          !route.requiredAnyPermissions.some(permissionCode =>
            hasPermission(permissionCode)
          )
        ) {
          return null;
        }

        return route;
      })
      .filter(Boolean);

  const getBaseRouteGroups = () => {
    if (currentMode === WORKSPACE_MODES.PARTNERS) {
      return [
        {
          ...workspaceRoutes,
          children: workspaceRoutes.children.filter(route => route.modes?.includes(WORKSPACE_MODES.PARTNERS))
        }
      ];
    }

    if (currentMode === WORKSPACE_MODES.BLOG) {
      return [
        {
          ...workspaceRoutes,
          children: workspaceRoutes.children.filter(route => route.modes?.includes(WORKSPACE_MODES.BLOG))
        }
      ];
    }

    if (currentMode === WORKSPACE_MODES.SUPERADMIN) {
      return [
        metricsRoutes,
        workspaceRoutes,
        marketplaceRoutes,
        operationsRoutes,
        configurationRoutes,
        accessRoutes
      ];
    }

    return [
      metricsRoutes,
      workspaceRoutes,
      marketplaceRoutes,
      operationsRoutes,
      configurationRoutes,
      accessRoutes
    ];
  };

  const visibleRoutes = getBaseRouteGroups()
    .map(routeGroup => ({
      ...routeGroup,
      children: filterRoutesByPermission(routeGroup.children || [])
    }))
    .filter(routeGroup => (routeGroup.children || []).length > 0);

  const NavbarLabel = ({ label }) => (
    <Nav.Item as="li">
      <Row className="mt-3 mb-2 navbar-vertical-label-wrapper">
        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
          {label}
        </Col>
        <Col className="ps-0">
          <hr className="mb-0 navbar-vertical-divider"></hr>
        </Col>
      </Row>
    </Nav.Item>
  );

  return (
    <Navbar
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      variant="light"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" width={40} />
      </Flex>
      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none'
        }}
      >
        <div className="navbar-vertical-content scrollbar">
          <Nav className="flex-column" as="ul">
            {visibleRoutes.map(route => (
              <Fragment key={route.label}>
                {!route.labelDisable && (
                  <NavbarLabel label={capitalize(t(route.label))} />
                )}
                <NavbarVerticalMenu routes={route.children} />
              </Fragment>
            ))}
          </Nav>

          <>
            {navbarPosition === 'combo' && (
              <div className={`d-${topNavbarBreakpoint}-none`}>
                <div className="navbar-vertical-divider">
                  <hr className="navbar-vertical-hr my-2" />
                </div>
                <Nav navbar>
                  <NavbarTopDropDownMenus />
                </Nav>
              </div>
            )}
          </>
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
